import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logoutUser } from '../store/slices/userSlice';
import { FaHouseDamage, FaAddressCard, FaSignOutAlt, FaUsers } from 'react-icons/fa';
import { MdSettingsInputComponent, MdPrivacyTip } from 'react-icons/md';
import { IoMdAnalytics } from 'react-icons/io';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import ConfirmModal from '../components/ConfirmModal';

const AppHeader: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset image error when user or avatarUrl changes
  useEffect(() => {
    setProfileImageError(false);
  }, [user?.avatarUrl]);

  // Don't show header on landing page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <header className="bg-white border-b border-[#F0F0F0] sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center justify-center h-full hover:opacity-80 transition-opacity">
          <img 
            src="/focuzon.png" 
            alt="Focuzon Logo" 
            className="h-8 w-auto object-contain"
            style={{ maxWidth: '140px', minWidth: '100px' }}
          />
        </Link>

        {/* Profile Icon - Simplified */}
        <div className="relative" ref={profileMenuRef}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#FF5924]/20"
          >
            {user?.avatarUrl && !profileImageError ? (
              <div className="w-full h-full rounded-full overflow-hidden ring-1 ring-[#F0F0F0]">
                <img
                  src={user.avatarUrl}
                  alt={user?.name || 'Profile'}
                  className="w-full h-full object-cover"
                  onError={() => setProfileImageError(true)}
                />
              </div>
            ) : (
              <div className="w-full h-full bg-[#FF5924] rounded-full flex items-center justify-center text-white font-medium text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            )}
          </button>

          {/* Profile Menu Popup - Cleaner */}
          {showProfileMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-[#F0F0F0] shadow-lg z-50 overflow-hidden">
              <div className="py-1">
                <div className="px-4 py-3 border-b border-[#F0F0F0]">
                  <div className="text-[#1A1A1A] font-medium text-sm">{user?.name || 'User'}</div>
                  <div className="text-[#6B6B6B] text-xs mt-0.5">{user?.email || 'user@example.com'}</div>
                </div>

                <Link
                  to="/dashboard"
                  onClick={() => setShowProfileMenu(false)}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
                >
                  <FaHouseDamage className="text-base text-[#FF5924]" />
                  <span className="text-[#1A1A1A] text-sm">Dashboard</span>
                </Link>
                <Link
                  to="/team"
                  onClick={() => setShowProfileMenu(false)}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
                >
                  <FaUsers className="text-base text-[#6B6B6B]" />
                  <span className="text-[#1A1A1A] text-sm">Team Mode</span>
                </Link>
                <Link
                  to="/account-settings"
                  onClick={() => setShowProfileMenu(false)}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
                >
                  <MdSettingsInputComponent className="text-base text-[#6B6B6B]" />
                  <span className="text-[#1A1A1A] text-sm">Settings</span>
                </Link>
                <Link
                  to="/worklog"
                  onClick={() => setShowProfileMenu(false)}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
                >
                  <IoMdAnalytics className="text-base text-[#FF5924]" />
                  <span className="text-[#1A1A1A] text-sm">Analytics</span>
                </Link>
                {/* <Link
                  to="/subscription"
                  onClick={() => setShowProfileMenu(false)}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
                >
                  <FaAddressCard className="text-base text-[#FF5924]" />
                  <span className="text-[#1A1A1A] text-sm">Subscription</span>
                </Link> */}
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowPrivacyModal(true);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors"
                >
                  <MdPrivacyTip className="text-base text-[#6B6B6B]" />
                  <span className="text-[#1A1A1A] text-sm">Privacy</span>
                </button>
                <div className="border-t border-[#F0F0F0] my-1"></div>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowLogoutConfirm(true);
                  }}
                  disabled={isLoggingOut}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-left hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaSignOutAlt className="text-base text-red-500" />
                  <span className="text-[#1A1A1A] text-sm">
                    {isLoggingOut ? 'Signing out...' : 'Sign Out'}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />

      {/* Logout Confirmation Modal */}
      <ConfirmModal
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={async () => {
          setIsLoggingOut(true);
          try {
            await dispatch(logoutUser()).unwrap();
            navigate('/login');
          } catch (error) {
            console.error('Logout error:', error);
            navigate('/login');
          } finally {
            setIsLoggingOut(false);
            setShowLogoutConfirm(false);
          }
        }}
        title="Sign Out"
        message="Are you sure you want to sign out of your account?"
        confirmText="Sign Out"
        cancelText="Cancel"
        type="danger"
        preventCloseOnOutsideClick={true}
        isLoading={isLoggingOut}
      />
    </header>
  );
};

export default AppHeader;
